import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // 主 CTA：深玫瑰粉 pill
        default: "bg-primary text-primary-foreground hover:bg-[hsl(var(--accent-hover))] active:bg-[hsl(var(--accent-active))] disabled:bg-[hsl(var(--accent-disabled))] shadow-soft-warm",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // 次 CTA：透明底 + 玫瑰粉外框 + 玫瑰粉文字
        outline: "border border-primary bg-transparent text-primary hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "rounded-md hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Brand Variants
        hero: "bg-primary text-primary-foreground hover:bg-[hsl(var(--accent-hover))] active:bg-[hsl(var(--accent-active))] shadow-elevated hover:shadow-glow text-base tracking-wide",
        gold: "bg-primary text-primary-foreground hover:bg-[hsl(var(--accent-hover))] shadow-soft-warm",
        elegant: "bg-transparent border border-foreground/20 text-foreground hover:bg-foreground/5 hover:border-foreground/40 tracking-widest uppercase text-xs",
        // 後台用：保留深色按鈕避免管理介面變粉
        admin: "bg-foreground text-background hover:bg-foreground/90 rounded-md",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 px-4 text-xs",
        lg: "h-12 px-8 text-base",
        xl: "h-14 px-10 text-lg",
        icon: "h-10 w-10 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
